<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Payment In" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<form @submit.prevent="submit">
						<div class="row">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.type" @change="onTypeChange">
									<option value="">— Select —</option>
									<option value="payment_in">Payment In</option>
									<option value="contra">Contra (bank to bank)</option>
									<option value="repayment">Repayment</option>
								</select>
								<div v-if="errors.type" class="text-danger small mt-1">{{ errors.type }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Amount (₹)<span class="text-danger">*</span></label>
								<input class="form-control" type="number" min="0" step="0.01" v-model="form.amount" placeholder="0.00" />
								<small v-if="form.type === 'repayment' && form.payment_fors.length" class="text-muted">
									Updates when you change allocation amounts; you can adjust manually if needed.
								</small>
								<div v-if="errors.amount" class="text-danger small mt-1">{{ errors.amount }}</div>
							</div>

							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date<span class="text-danger">*</span></label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>
						</div>

						<div class="row" v-if="form.type !== 'repayment'">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID</label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>

							<div class="col-md-8 form-group mt-2">
								<label class="control-label">Remarks</label>
								<input class="form-control" type="text" v-model="form.remarks" placeholder="Optional remarks" />
								<div v-if="errors.remarks" class="text-danger small mt-1">{{ errors.remarks }}</div>
							</div>
						</div>

						<!-- Contra -->
						<div v-if="form.type === 'contra'" class="mt-3">
							<h6 class="border-bottom pb-2">Contra payment (bank to bank)</h6>

							<div class="row">
								<div class="col-md-6 form-group mt-2">
									<label class="control-label">From bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.from_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="'f-' + b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<div v-if="errors.from_account_id" class="text-danger small mt-1">{{ errors.from_account_id }}</div>
								</div>

								<div class="col-md-6 form-group mt-2">
									<label class="control-label">To bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.to_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="'t-' + b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<div v-if="errors.to_account_id" class="text-danger small mt-1">{{ errors.to_account_id }}</div>
								</div>
							</div>

							<div v-if="errors.same_account" class="text-danger small mt-2">{{ errors.same_account }}</div>
						</div>

						<!-- Repayment (userPayment API) -->
						<div v-if="form.type === 'repayment'" class="mt-3">
							<h6 class="border-bottom pb-2">Repayment</h6>

							<div class="row">
								<div class="col-md-12 form-group mt-2">
									<label class="control-label">Payment for<span class="text-danger">*</span></label>
									<div class="d-flex flex-wrap gap-3 mt-1">
										<div class="form-check">
											<input
												class="form-check-input"
												type="checkbox"
												id="paymentForRenewal"
												value="1"
												v-model="form.payment_fors"
												@change="onRepaymentPaymentForChange"
											/>
											<label class="form-check-label" for="paymentForRenewal">Renewal repayment</label>
										</div>
										<div class="form-check">
											<input
												class="form-check-input"
												type="checkbox"
												id="paymentForService"
												value="2"
												v-model="form.payment_fors"
												@change="onRepaymentPaymentForChange"
											/>
											<label class="form-check-label" for="paymentForService">Work service repayment</label>
										</div>
										<div class="form-check">
											<input
												class="form-check-input"
												type="checkbox"
												id="paymentForSales"
												value="4"
												v-model="form.payment_fors"
												@change="onRepaymentPaymentForChange"
											/>
											<label class="form-check-label" for="paymentForSales">Sales repayment</label>
										</div>
									</div>
									<small class="text-muted d-block mt-1">You can select one or more. Pending items load after you pick a dealer/customer.</small>
									<div v-if="errors.payment_for" class="text-danger small mt-1">{{ errors.payment_for }}</div>
								</div>
							</div>

							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">User type<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.user_type" @change="onRepaymentUserTypeChange">
										<option value="">— Select —</option>
										<option value="1">Dealer</option>
										<option value="2">Customer</option>
									</select>
									<div v-if="errors.user_type" class="text-danger small mt-1">{{ errors.user_type }}</div>
								</div>

								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.bank_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="'rp-' + b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
								</div>
							</div>

							<div class="row" v-if="form.user_type === '1'">
								<div class="col-md-6 form-group mt-2 position-relative">
									<label class="control-label">Dealer<span class="text-danger">*</span></label>
									<input
										type="text"
										class="form-control"
										v-model="dealerSearch"
										placeholder="Search dealer…"
										@focus="showDealerSuggestions = true"
										@input="showDealerSuggestions = true"
										@blur="hideDealerSuggestions"
									/>
									<div v-if="errors.dealer_id" class="text-danger small mt-1">{{ errors.dealer_id }}</div>
									<div v-if="showDealerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
										<button
											v-for="d in filteredDealers"
											:key="'rd-' + d.id"
											type="button"
											class="list-group-item list-group-item-action"
											@click="selectDealerForRepayment(d)"
										>
											{{ d.first_name }}
										</button>
										<div v-if="!filteredDealers.length" class="list-group-item text-muted">No matches</div>
									</div>
									<small v-if="selectedDealer" class="text-muted d-block mt-1">Selected: {{ selectedDealer.first_name }}</small>
								</div>
							</div>

							<div class="row" v-if="form.user_type === '2'">
								<div class="col-md-6 form-group mt-2 position-relative">
									<label class="control-label">Customer<span class="text-danger">*</span></label>
									<input
										type="text"
										class="form-control"
										v-model="customerSearch"
										placeholder="Search customer by company / mobile…"
										@focus="showCustomerSuggestions = true"
										@input="showCustomerSuggestions = true"
										@blur="hideCustomerSuggestions"
									/>
									<div v-if="errors.customer_id" class="text-danger small mt-1">{{ errors.customer_id }}</div>
									<div v-if="showCustomerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
										<button
											v-for="c in filteredCustomers"
											:key="'rc-' + c.id"
											type="button"
											class="list-group-item list-group-item-action"
											@click="selectCustomerForRepayment(c)"
										>
											<div class="d-flex justify-content-between">
												<span>{{ c.company_name }}</span>
												<small class="text-muted">{{ c.mobile || "—" }}</small>
											</div>
										</button>
										<div v-if="!filteredCustomers.length" class="list-group-item text-muted">No matches</div>
									</div>
									<small v-if="selectedCustomer" class="text-muted d-block mt-1">
										Selected: {{ selectedCustomer.company_name }}
										{{ selectedCustomer.mobile ? "(" + selectedCustomer.mobile + ")" : "" }}
									</small>
								</div>
							</div>

							<div v-if="repaymentUserId" class="card bg-light mt-3 mb-0">
								<div class="card-body py-3">
									<div class="row text-center">
										<div class="col-md-3">
											<small class="text-muted d-block">Pending renewals</small>
											<span class="fw-bold text-danger">₹{{ formatMoney(totalRenewalPendingAmount) }}</span>
										</div>
										<div class="col-md-3">
											<small class="text-muted d-block">Pending services</small>
											<span class="fw-bold text-danger">₹{{ formatMoney(totalServicePendingAmount) }}</span>
										</div>
										<div class="col-md-3">
											<small class="text-muted d-block">Pending sales</small>
											<span class="fw-bold text-danger">₹{{ formatMoney(totalSalesPendingAmount) }}</span>
										</div>
										<div class="col-md-3">
											<small class="text-muted d-block">This payment</small>
											<span class="fw-bold text-primary">₹{{ formatMoney(form.amount) }}</span>
										</div>
									</div>
								</div>
							</div>

							<div v-if="!repaymentUserId && form.user_type" class="alert alert-light border small mt-3 mb-0">
								Select a {{ form.user_type === "1" ? "dealer" : "customer" }} to load pending items.
							</div>

							<div v-if="repaymentUserId && loadingRepaymentPending" class="text-center py-4 mt-3">
								<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								<p class="mt-2 text-muted small mb-0">Loading pending items…</p>
							</div>

							<!-- Renewal allocations -->
							<div v-if="repaymentUserId && hasPaymentFor('1') && !loadingRepaymentPending" class="mt-3 position-relative">
								<div
									v-if="refreshingRepaymentLists"
									class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
									style="z-index: 2"
								>
									<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								</div>
								<h6 class="border-bottom pb-2">Apply to pending renewals</h6>
								<div class="row mb-2">
									<div class="col-md-8 col-lg-6">
										<input
											type="text"
											class="form-control form-control-sm"
											placeholder="Search renewals (IMEI, vehicle…) — updates as you type"
											v-model="renewalKeyword"
											@input="onRenewalKeywordInput"
										/>
									</div>
								</div>
								<div class="table-responsive">
									<table class="table table-sm table-bordered align-middle">
										<thead class="table-light">
											<tr align="center">
												<th>IMEI</th>
												<th>Vehicle</th>
												<th>Payment for</th>
												<th>Payment date</th>
												<th>Balance due</th>
												<th style="min-width: 120px">Apply (₹)</th>
											</tr>
										</thead>
										<tbody v-if="renewalPaymentRows.length > 0">
											<tr v-for="row in renewalPaymentRows" :key="'ap-' + row.id" align="center">
												<td class="text-start small">{{ row.imei || "—" }}</td>
												<td>{{ row.vehicle_no || "—" }}</td>
												<td>{{ renewalAiroPaymentForLabel(row.payment_for) }}</td>
												<td class="small">{{ row.payment_date || "—" }}</td>
												<td>₹{{ formatMoney(row.balance_amount) }}</td>
												<td>
													<input
														type="number"
														class="form-control form-control-sm"
														min="0"
														step="0.01"
														:placeholder="'max ' + formatMoney(row.balance_amount)"
														v-model="renewalAmounts[row.id]"
														@input="syncRepaymentAmountFromAllocations"
													/>
												</td>
											</tr>
										</tbody>
										<tbody v-else>
											<tr>
												<td colspan="6" class="text-center text-muted py-3">No pending renewal payments</td>
											</tr>
										</tbody>
									</table>
								</div>
								<nav v-if="renewalPagination?.length" class="d-flex justify-content-end">
									<ul class="pagination pagination-sm mb-0">
										<li
											class="page-item"
											v-for="(page, idx) in renewalPagination"
											:key="'rp-' + idx"
											:class="{ disabled: !page.url, active: page.active }"
										>
											<a class="page-link" href="#" @click.prevent="onRenewalPage(page)">{{ formatPaginationLabel(page.label) }}</a>
										</li>
									</ul>
								</nav>
								<p class="small mb-0 mt-2">Allocated: <strong>₹{{ formatMoney(sumRenewalAllocated) }}</strong></p>
							</div>

							<!-- Service allocations -->
							<div v-if="repaymentUserId && hasPaymentFor('2') && !loadingRepaymentPending" class="mt-3 position-relative">
								<div
									v-if="refreshingRepaymentLists"
									class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
									style="z-index: 2"
								>
									<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								</div>
								<h6 class="border-bottom pb-2">Apply to pending work services</h6>
								<div class="row mb-2">
									<div class="col-md-8 col-lg-6">
										<input
											type="text"
											class="form-control form-control-sm"
											placeholder="Search services…"
											v-model="serviceKeyword"
											@input="onServiceKeywordInput"
										/>
									</div>
								</div>
								<div class="table-responsive">
									<table class="table table-sm table-bordered align-middle">
										<thead class="table-light">
											<tr align="center">
												<th>Service</th>
												<th>Details</th>
												<th>Balance due</th>
												<th style="min-width: 120px">Apply (₹)</th>
											</tr>
										</thead>
										<tbody v-if="serviceRows.length">
											<tr v-for="row in serviceRows" :key="'s-' + row.id" align="center">
												<td class="small">{{ serviceTypeLabel(row.service_type) }}</td>
												<td class="text-start small">{{ row.details || "—" }}</td>
												<td>₹{{ formatMoney(row.balance_amount) }}</td>
												<td>
													<input
														type="number"
														class="form-control form-control-sm"
														min="0"
														step="0.01"
														v-model="serviceAmounts[row.id]"
														@input="syncRepaymentAmountFromAllocations"
													/>
												</td>
											</tr>
										</tbody>
										<tbody v-else>
											<tr>
												<td colspan="4" class="text-center text-muted py-3">No pending work services</td>
											</tr>
										</tbody>
									</table>
								</div>
								<nav v-if="servicePagination.length" class="d-flex justify-content-end">
									<ul class="pagination pagination-sm mb-0">
										<li
											class="page-item"
											v-for="(page, idx) in servicePagination"
											:key="'sp-' + idx"
											:class="{ disabled: !page.url, active: page.active }"
										>
											<a class="page-link" href="#" @click.prevent="onServicePage(page)">{{ formatPaginationLabel(page.label) }}</a>
										</li>
									</ul>
								</nav>
								<p class="small mb-0 mt-2">Allocated: <strong>₹{{ formatMoney(sumServiceAllocated) }}</strong></p>
							</div>

							<!-- Sales allocations -->
							<div v-if="repaymentUserId && hasPaymentFor('4') && !loadingRepaymentPending" class="mt-3 position-relative">
								<div
									v-if="refreshingRepaymentLists"
									class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-start justify-content-center pt-5 bg-white bg-opacity-75"
									style="z-index: 2"
								>
									<div class="spinner-border spinner-border-sm text-primary" role="status"></div>
								</div>
								<h6 class="border-bottom pb-2">Apply to pending sales</h6>
								<div class="row mb-2">
									<div class="col-md-8 col-lg-6">
										<input
											type="text"
											class="form-control form-control-sm"
											placeholder="Search sales (invoice, bank ref…)…"
											v-model="salesKeyword"
											@input="onSalesKeywordInput"
										/>
									</div>
								</div>
								<div class="table-responsive">
									<table class="table table-sm table-bordered align-middle">
										<thead class="table-light">
											<tr align="center">
												<th>Invoice</th>
												<th>Total</th>
												<th>Paid amount</th>
												<th>Balance due</th>
												<th style="min-width: 120px">Apply (₹)</th>
											</tr>
										</thead>
										<tbody v-if="salesRows.length">
											<tr v-for="row in salesRows" :key="'sl-' + row.id" align="center">
												<td class="text-start small">
													{{ row.invoice_number || "—" }}
													<span v-if="row.invoice_date" class="text-muted">{{ row.invoice_date }}</span>
												</td>
												<td>₹{{ formatMoney(row.total_amount) }}</td>
												<td>₹{{ formatMoney(row.paid_amount) }}</td>
												<td>₹{{ formatMoney(row.balance_amount) }}</td>
												<td>
													<input
														type="number"
														class="form-control form-control-sm"
														min="0"
														step="0.01"
														v-model="salesAmounts[row.id]"
														@input="syncRepaymentAmountFromAllocations"
													/>
												</td>
											</tr>
										</tbody>
										<tbody v-else>
											<tr>
												<td colspan="5" class="text-center text-muted py-3">No pending sales</td>
											</tr>
										</tbody>
									</table>
								</div>
								<nav v-if="salesPagination.length" class="d-flex justify-content-end">
									<ul class="pagination pagination-sm mb-0">
										<li
											class="page-item"
											v-for="(page, idx) in salesPagination"
											:key="'slp-' + idx"
											:class="{ disabled: !page.url, active: page.active }"
										>
											<a class="page-link" href="#" @click.prevent="onSalesPage(page)">{{ formatPaginationLabel(page.label) }}</a>
										</li>
									</ul>
								</nav>
								<p class="small mb-0 mt-2">Allocated: <strong>₹{{ formatMoney(sumSalesAllocated) }}</strong></p>
							</div>

							<div v-if="repaymentUserId && form.payment_fors.length" class="mt-4">
								<h6 class="border-bottom pb-2">Payment method</h6>
								<div class="row">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Payment type<span class="text-danger">*</span></label>
										<select class="form-select" v-model="form.repayment_payment_type" @change="onRepaymentPaymentTypeChange">
											<option value="">— Select —</option>
											<option value="1">Cash</option>
											<option value="2">Online</option>
											<option value="3">Both</option>
											<option v-if="!hasPaymentFor('4')" value="4">Advance</option>
										</select>
										<div v-if="errors.repayment_payment_type" class="text-danger small mt-1">{{ errors.repayment_payment_type }}</div>
									</div>
									<div class="col-md-4 form-group mt-2" v-if="form.repayment_payment_type === '2' || form.repayment_payment_type === '3'">
										<label class="control-label">Payment / transaction ID</label>
										<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
										<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
									</div>
								</div>

								<div class="row" v-if="showRepaymentAdvanceFromCredit">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Amount from advance<span class="text-danger">*</span></label>
										<input
											class="form-control"
											type="number"
											step="0.01"
											min="0"
											v-model="form.amount_from_credit"
											:class="{ 'is-invalid': errors.amount_from_credit }"
										/>
										<div v-if="userCreditLoading" class="mt-2"><small class="text-success">Loading…</small></div>
										<div v-else-if="userCredit && userCredit.credit_available_amount != null" class="mt-2">
											<small class="text-success">Advance available: ₹{{ userCredit.credit_available_amount }}</small>
										</div>
										<div v-if="errors.amount_from_credit" class="invalid-feedback d-block">{{ errors.amount_from_credit }}</div>
										<div v-if="userCreditError" class="text-danger small mt-1">{{ userCreditError }}</div>
									</div>
								</div>

								<div class="row" v-if="form.repayment_payment_type === '1' || form.repayment_payment_type === '3'">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Cash Amount (₹)<span class="text-danger">*</span></label>
										<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_in_hand" />
										<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
									</div>
								</div>
								<div class="row" v-if="form.repayment_payment_type === '2' || form.repayment_payment_type === '3'">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Cash online (₹)<span class="text-danger">*</span></label>
										<input class="form-control" type="number" min="0" step="0.01" v-model="form.cash_online" />
										<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
									</div>
								</div>
							</div>
						</div>

						<!-- Payment In -->
						<div v-if="form.type === 'payment_in'" class="mt-3">
							<h6 class="border-bottom pb-2">Payment In</h6>

							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">User type<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.user_type" @change="onUserTypeChange">
										<option value="">— Select —</option>
										<option value="1">Dealer</option>
										<option value="2">Customer</option>
										<!-- <option value="3">Vendor</option> -->
										<option value="4">Cash Sale (Other party)</option>
										<!-- <option value="5">Staff</option> -->
									</select>
									<div v-if="errors.user_type" class="text-danger small mt-1">{{ errors.user_type }}</div>
								</div>

								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.bank_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="'bi-' + b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
								</div>
							</div>

							<!-- Dealer searchable -->
							<div class="row" v-if="form.user_type === '1'">
								<div class="col-md-6 form-group mt-2 position-relative">
									<label class="control-label">Dealer<span class="text-danger">*</span></label>
									<input
										type="text"
										class="form-control"
										v-model="dealerSearch"
										placeholder="Search dealer…"
										@focus="showDealerSuggestions = true"
										@input="showDealerSuggestions = true"
										@blur="hideDealerSuggestions"
									/>
									<div v-if="errors.dealer_id" class="text-danger small mt-1">{{ errors.dealer_id }}</div>

									<div v-if="showDealerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
										<button
											v-for="d in filteredDealers"
											:key="'d-' + d.id"
											type="button"
											class="list-group-item list-group-item-action"
											@click="selectDealer(d)"
										>
											{{ d.first_name }}
										</button>
										<div v-if="!filteredDealers.length" class="list-group-item text-muted">No matches</div>
									</div>
									<small v-if="selectedDealer" class="text-muted d-block mt-1">Selected: {{ selectedDealer.first_name }}</small>
								</div>
							</div>

							<!-- Customer — existing -->
							<div class="row" v-if="form.user_type === '2' && !isNewCustomer">
								<div class="col-md-6 form-group mt-2 position-relative">
									<label class="control-label">Customer<span class="text-danger">*</span></label>
									<input
										type="text"
										class="form-control"
										v-model="customerSearch"
										placeholder="Search customer by company / mobile…"
										@focus="showCustomerSuggestions = true"
										@input="showCustomerSuggestions = true"
										@blur="hideCustomerSuggestions"
									/>
									<div v-if="errors.customer_id" class="text-danger small mt-1">{{ errors.customer_id }}</div>

									<div v-if="showCustomerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
										<button
											v-for="c in filteredCustomers"
											:key="'c-' + c.id"
											type="button"
											class="list-group-item list-group-item-action"
											@click="selectCustomer(c)"
										>
											<div class="d-flex justify-content-between">
												<span>{{ c.company_name }}</span>
												<small class="text-muted">{{ c.mobile || "—" }}</small>
											</div>
										</button>
										<div v-if="!filteredCustomers.length" class="list-group-item text-muted">No matches</div>
									</div>
									<small v-if="selectedCustomer" class="text-muted d-block mt-1">
										Selected: {{ selectedCustomer.company_name }} {{ selectedCustomer.mobile ? '(' + selectedCustomer.mobile + ')' : '' }}
									</small>
									<div class="mt-2">
										<a href="#" class="small" @click.prevent="enableNewCustomer">+ Add new customer</a>
									</div>
								</div>
							</div>

							<!-- Customer — new -->
							<div class="row" v-if="form.user_type === '2' && isNewCustomer">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Customer name<span class="text-danger">*</span></label>
									<input class="form-control" type="text" v-model="form.customer_name" placeholder="Enter customer name" />
									<div v-if="errors.customer_name" class="text-danger small mt-1">{{ errors.customer_name }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Customer mobile<span class="text-danger">*</span></label>
									<input class="form-control" v-model="form.customer_mobile" v-mobile-limit placeholder="Enter customer mobile" maxlength="10" minlength="10" />
									<div v-if="errors.customer_mobile" class="text-danger small mt-1">{{ errors.customer_mobile }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Password<span class="text-danger">*</span></label>
									<input class="form-control" type="text" v-model="form.password" disabled />
									<div v-if="errors.password" class="text-danger small mt-1">{{ errors.password }}</div>
								</div>
								<div class="col-md-12">
									<a href="#" class="small" @click.prevent="selectExistingCustomer">← Select existing customer</a>
								</div>
							</div>

							<!-- Other party -->
							<div class="row" v-if="form.user_type === '4'">
								<div class="col-md-6 form-group mt-2">
									<label class="control-label">Customer name</label>
									<input class="form-control" type="text" v-model="form.customer_name" placeholder="Optional name" />
									<div v-if="errors.customer_name" class="text-danger small mt-1">{{ errors.customer_name }}</div>
								</div>
								<div class="col-md-6 form-group mt-2">
									<label class="control-label">Customer mobile</label>
									<input class="form-control" v-model="form.customer_mobile" v-mobile-limit placeholder="Optional mobile" maxlength="10" minlength="10" />
									<div v-if="errors.customer_mobile" class="text-danger small mt-1">{{ errors.customer_mobile }}</div>
								</div>
							</div>

							<div v-if="paymentInUserId" class="card bg-light mt-3 mb-0">
								<div class="card-body py-3">
									<div v-if="userDueLoading" class="text-center small text-muted">
										<span class="spinner-border spinner-border-sm me-2"></span>
										Loading credit balance…
									</div>
									<div v-else-if="userDueError" class="text-danger small">{{ userDueError }}</div>
									<div v-else class="row text-center">
										<div class="col-md-6">
											<small class="text-muted d-block">Due amount</small>
											<span class="fw-bold text-danger">₹{{ formatMoney(userDueCreditDue) }}</span>
										</div>
										<div class="col-md-6">
											<small class="text-muted d-block">Advance available</small>
											<span class="fw-bold text-success">₹{{ formatMoney(userDueCreditAvailable) }}</span>
										</div>
									</div>
								</div>
							</div>

							<div v-if="paymentInUserId && !userDueLoading" class="row mt-3">
								<div class="col-md-12 form-group">
									<div class="form-check">
										<input
											class="form-check-input"
											type="checkbox"
											id="applyToDue"
											v-model="form.apply_to_due"
											@change="onApplyToDueChange"
										/>
										<label class="form-check-label" for="applyToDue">Apply to due</label>
									</div>
								</div>
								<div class="col-md-6 form-group mt-2" v-if="form.apply_to_due">
									<label class="control-label">Amount for due<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.amount_for_due"
										placeholder="0.00"
									/>
									<small class="text-muted d-block mt-1">
										Cannot exceed due (₹{{ formatMoney(userDueCreditDue) }}) or payment amount.
									</small>
									<div v-if="errors.amount_for_due" class="text-danger small mt-1">{{ errors.amount_for_due }}</div>
								</div>
								<div class="col-md-12" v-if="form.apply_to_due && paymentInDueBreakdown">
									<div class="card border-light">
										<div class="card-body py-2">
											<div class="row text-center small">
												<div class="col-md-4">
													<span class="text-muted d-block">Payment amount</span>
													<span class="fw-bold">₹{{ paymentInDueBreakdown.total }}</span>
												</div>
												<div class="col-md-4">
													<span class="text-muted d-block">Apply to due</span>
													<span class="fw-bold text-danger">₹{{ paymentInDueBreakdown.due }}</span>
												</div>
												<div class="col-md-4">
													<span class="text-muted d-block">Deposit (advance)</span>
													<span class="fw-bold text-success">₹{{ paymentInDueBreakdown.deposit }}</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Submit" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="resetAll">Reset</button>
							</div>
						</div>
					</form>
				</div>
			</div>

			<div class="card mt-4">
				<div class="card-body">
					<h5 class="card-title mb-3">Payment in records</h5>

					<div class="col-md-12 project-list filter-sort">
						<div class="row align-items-end">
							<div class="col-md-4 d-flex mt-5">
								<label class="d-flex listing-length ms-1 align-items-center">
									<span class="me-2">Show</span>
									<select class="form-select mx-2" style="width: auto" v-model="paymentInList.listing_length" @change="fetchPaymentInList()">
										<option value="5">5</option>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</select>
									<span class="ms-2">entries</span>
								</label>
							</div>
							<div class="col-lg-2 col-md-4 mt-2">
								<select class="form-select" v-model="paymentInList.transaction_type" @change="fetchPaymentInList()">
									<option value="">All</option>
									<option value="1">Contra</option>
									<option value="2">Deposit</option>
									<option value="4">Sales</option>
								</select>
							</div>
							<div class="col-lg-2 col-md-4 mt-2">
								<select class="form-select" v-model="paymentInList.bank_account_id" @change="fetchPaymentInList()">
									<option value="">All banks</option>
									<option v-for="b in bankAccounts" :key="'lst-' + b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
							</div>
							<div class="col-lg-2 col-md-4 mt-2">
								<input class="form-control" type="date" v-model="paymentInList.date" @change="fetchPaymentInList()" />
							</div>
							<div class="col-lg-2 col-md-4 mt-2">
								<input
									class="form-control"
									type="text"
									placeholder="Keyword…"
									v-model="paymentInList.keyword"
									@input="onListKeywordInput"
								/>
							</div>
						</div>
					</div>

					<div class="table-responsive product-table mt-3">
						<table class="display table table-sm">
							<thead>
								<tr align="center">
									<th>Sl.No</th>
									<th>Transaction amount</th>
									<th>Payment type</th>
									<th>Transaction type</th>
									<th>User type</th>
									<th>User</th>
									<th>Bank</th>
									<th>Remarks</th>
									<th>Added by</th>
									<th>Payment ID</th>
									<th>Payment Date</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody v-if="paymentInList.loading">
								<tr>
									<td colspan="12" align="center">
										<span class="spinner-border spinner-border-sm me-2"></span>
										Loading…
									</td>
								</tr>
							</tbody>
							<tbody v-else-if="paymentInList.rows.length">
								<tr align="center" v-for="(row, index) in paymentInList.rows" :key="'pi-' + row.id">
									<td>{{ paymentInList.from != null ? paymentInList.from + index : index + 1 }}</td>
									<td class="text-start small">
										<div><span class="text-muted">Total Amount:</span> ₹{{ formatMoney(row.amount) }}</div>
									</td>
									<td>
										<span class="badge rounded-pill bg-info text-dark">{{ paymentTypeRecordLabel(row.payment_type) }}</span>
									</td>
									<td>
										<span class="badge rounded-pill bg-secondary">{{ transactionTypeRecordLabel(row.transaction_type) }}</span>
									</td>
									<td>{{ userTypeRecordLabel(row.user_type) }}</td>
									<td class="text-start small">
										<div>{{ row.dealer_name || row.admin_name || row.user_name || "—" }}</div>
										<div class="text-muted">{{ row.user_mobile || row.users_mobile || "—" }}</div>
									</td>
									<td class="text-start small">
										<template v-if="String(row.payment_type) === '3'">
											<div><span class="text-muted">From:</span> {{ row.from_account_bank_name || "—" }}</div>
											<div><span class="text-muted">To:</span> {{ row.to_account_bank_name || "—" }}</div>
										</template>
										<template v-else>
											{{ row.to_account_bank_name || row.from_account_bank_name || "—" }}
										</template>
									</td>
									<td class="text-start small">{{ row.remarks || "—" }}</td>
									<td class="text-start small">{{ row.added_by_name || "—" }}</td>
									<td class="text-start small">{{ row.payment_id || "—" }}</td>
									<td>{{ row.payment_date ? $filters.formatDate(row.payment_date) : "—" }}</td>
									<td>
										<button
											type="button"
											class="btn btn-sm btn-outline-secondary me-1 mb-1"
											@click="openPaymentInLogModal(row)"
										>
											Payment Log
										</button>
										<button
											v-if="$canRevoke()"
											type="button"
											class="btn btn-sm btn-outline-danger mb-1"
											@click="openRevokePaymentModal(row)"
										>
											Revoke
										</button>
									</td>
								</tr>
							</tbody>
							<tbody v-else>
								<tr>
									<td colspan="12" align="center">No records found</td>
								</tr>
							</tbody>
						</table>
					</div>

					<caption v-if="paymentInList.rows.length" class="float-start mt-2 d-block">
						Showing {{ paymentInList.from }} to {{ paymentInList.to }} of {{ paymentInList.total }} entries
					</caption>
					<nav v-if="paymentInList.rows.length && paymentInList.pagination.length" aria-label="Payment in pagination" class="mt-2 float-end">
						<ul class="pagination mb-0">
							<li
								class="page-item"
								v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
								v-for="(page, index) in paymentInList.pagination"
								:key="'lpi-' + index + '-' + page.label"
							>
								<a class="page-link" href="#" @click.prevent="onPaymentInPageClick(page)" tabindex="-1">
									<span v-if="index === 0">Prev</span>
									<span v-else-if="index === paymentInList.pagination.length - 1">Next</span>
									<span v-else>{{ page.label }}</span>
								</a>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div
				v-if="paymentInLogModal.open && paymentInLogModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closePaymentInLogModal"
			>
				<div class="modal-dialog modal-xl modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Payment log</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closePaymentInLogModal"></button>
						</div>
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Transaction:</strong> {{ transactionTypeRecordLabel(paymentInLogModal.row.transaction_type) }}</div>
								<div><strong>User:</strong> {{ paymentInLogModal.row.user_name || paymentInLogModal.row.users_name || "—" }}
									<span v-if="paymentInLogModal.row.user_mobile || paymentInLogModal.row.users_mobile">
										({{ paymentInLogModal.row.user_mobile || paymentInLogModal.row.users_mobile }})
									</span>
								</div>
								<div><strong>Amount:</strong> ₹{{ formatMoney(paymentInLogModal.row.amount) }}</div>
								<div><strong>Payment date:</strong> {{ paymentInLogModal.row.payment_date ? $filters.formatDate(paymentInLogModal.row.payment_date) : "—" }}</div>
								<div><strong>Remarks:</strong> {{ paymentInLogModal.row.remarks || "—" }}</div>
							</div>

							<div v-if="paymentInLogModal.loading" class="text-center py-4">
								<span class="spinner-border spinner-border-sm me-2"></span>
								Loading payment logs…
							</div>
							<p v-else-if="paymentInLogModal.error" class="text-danger mb-0">{{ paymentInLogModal.error }}</p>
							<p v-else-if="!paymentInLogModal.logs.length" class="text-muted mb-0">No payment logs for this record.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>#</th>
											<th>IMEI</th>
											<th>Amount (₹)</th>
											<th>Total (₹)</th>
											<th>Pending (₹)</th>
											<th>Online (₹)</th>
											<th>Cash (₹)</th>
											<th>Credit (₹)</th>
											<th>Remarks</th>
											<th>Payment date</th>
											<th>Recorded on</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(log, idx) in paymentInLogModal.logs" :key="'pil-' + log.id" align="center">
											<td>{{ idx + 1 }}</td>
											<td class="text-start small">{{ log.imei || "—" }}</td>
											<td>₹{{ formatMoney(log.paid_amount ?? log.amount) }}</td>
											<td>₹{{ formatMoney(log.total_amount ?? log.total) }}</td>
											<td>₹{{ formatMoney(log.pending_amount) }}</td>
											<td>₹{{ formatMoney(log.online_amount) }}</td>
											<td>₹{{ formatMoney(log.cash_amount) }}</td>
											<td>₹{{ formatMoney(log.credit_amount) }}</td>
											<td class="text-start small">{{ log.remarks || "—" }}</td>
											<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
											<td class="text-start small">{{ log.converted_created_at || (log.created_at ? $filters.formatDate(log.created_at) : "—") }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" @click="closePaymentInLogModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="revokePaymentModal.open && revokePaymentModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closeRevokePaymentModal"
			>
				<div class="modal-dialog modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Revoke transaction</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeRevokePaymentModal"></button>
						</div>
						<div class="modal-body">
							<p class="mb-3">Are you sure you want to revoke this payment transaction?</p>
							<div class="small border rounded p-3 bg-light text-dark">
								<!-- <div class="mb-1"><strong>Transaction ID:</strong> #{{ revokePaymentModal.row.id }}</div> -->
								<div class="mb-1"><strong>Payment type:</strong> {{ paymentTypeRecordLabel(revokePaymentModal.row.payment_type) }}</div>
								<div class="mb-1"><strong>Transaction type:</strong> {{ transactionTypeRecordLabel(revokePaymentModal.row.transaction_type) }}</div>
								<div class="mb-1"><strong>User type:</strong> {{ userTypeRecordLabel(revokePaymentModal.row.user_type) }}</div>
								<div class="mb-1"><strong>User:</strong> {{ revokePaymentModal.row.user_name || revokePaymentModal.row.users_name || "—" }}</div>
								<div class="mb-1"><strong>Mobile:</strong> {{ revokePaymentModal.row.user_mobile || revokePaymentModal.row.users_mobile || "—" }}</div>
								<div class="mb-1"><strong>Total:</strong> ₹{{ formatMoney(revokePaymentModal.row.total) }}</div>
								<div class="mb-1"><strong>Bank:</strong>
									<template v-if="String(revokePaymentModal.row.payment_type) === '3'">
										{{ revokePaymentModal.row.from_account_bank_name || "—" }} → {{ revokePaymentModal.row.to_account_bank_name || "—" }}
									</template>
									<template v-else>
										{{ revokePaymentModal.row.to_account_bank_name || revokePaymentModal.row.from_account_bank_name || "—" }}
									</template>
								</div>
								<div class="mb-1"><strong>Payment ID:</strong> {{ revokePaymentModal.row.payment_id || "—" }}</div>
								<div class="mb-1"><strong>Payment date:</strong> {{ revokePaymentModal.row.payment_date ? $filters.formatDate(revokePaymentModal.row.payment_date) : "—" }}</div>
								<div class="mb-0"><strong>Remarks:</strong> {{ revokePaymentModal.row.remarks || "—" }}</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-danger" :disabled="revokePaymentModal.submitting" @click="confirmRevokePaymentIn">
								<span v-if="revokePaymentModal.submitting" class="spinner-border spinner-border-sm me-2"></span>
								Yes, revoke
							</button>
							<button type="button" class="btn btn-light" :disabled="revokePaymentModal.submitting" @click="closeRevokePaymentModal">No</button>
						</div>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
export default {
	name: "PaymentIn",
	data() {
		return {
			token: "",
			submitting: false,
			bankAccounts: [],
			dealers: [],
			customers: [],

			dealerSearch: "",
			customerSearch: "",
			selectedDealer: null,
			selectedCustomer: null,
			isNewCustomer: false,
			showDealerSuggestions: false,
			showCustomerSuggestions: false,

			errors: {},
			// Repayment (userPayment)
			loadingRepaymentPending: false,
			refreshingRepaymentLists: false,
			renewalSearchTimer: null,
			serviceSearchTimer: null,
			salesSearchTimer: null,
			totalRenewalPendingAmount: "0",
			totalServicePendingAmount: "0",
			totalSalesPendingAmount: "0",
			renewalRows: [],
			serviceRows: [],
			salesRows: [],
			renewalPagination: [],
			servicePagination: [],
			salesPagination: [],
			renewalKeyword: "",
			serviceKeyword: "",
			salesKeyword: "",
			renewalLimit: 500,
			serviceLimit: 200,
			salesLimit: 200,
			renewalAmounts: {},
			serviceAmounts: {},
			salesAmounts: {},
			userCredit: null,
			userCreditLoading: false,
			userCreditError: "",
			prevShowRepaymentAdvance: false,

			userDue: null,
			userDueLoading: false,
			userDueError: "",

			form: {
				type: "", // payment_in | contra | repayment
				user_type: "", // 1..5 (payment_in); 1–2 (repayment)
				amount: "",
				payment_id: "",
				payment_date: "",
				remarks: "",

				// contra
				from_account_id: "",
				to_account_id: "",

				// payment in / repayment
				bank_account_id: "",
				customer_name: "",
				customer_mobile: "",
				password: "123456",
				apply_to_due: false,
				amount_for_due: "",

				// repayment (userPayment) — multi: '1' renewal | '2' service | '4' sales
				payment_fors: [],
				repayment_payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
			},

			paymentInList: {
				listing_length: "10",
				/** Date ordering (sent as order=asc|desc with sort=created_at). */
				sort_date: "desc",
				transaction_type: "",
				bank_account_id: "",
				date: "",
				keyword: "",
				rows: [],
				pagination: [],
				from: 0,
				to: 0,
				total: 0,
				loading: false,
				active_page_url: "",
			},
			listKeywordDebounceTimer: null,

			paymentInLogModal: {
				open: false,
				row: null,
				logs: [],
				loading: false,
				error: "",
			},

			revokePaymentModal: {
				open: false,
				row: null,
				submitting: false,
			},
		};
	},
	computed: {
		repaymentUserId() {
			if (this.form.type !== "repayment") return null;
			if (this.form.user_type === "1" && this.selectedDealer?.user_id) {
				return parseInt(this.selectedDealer.user_id, 10);
			}
			if (this.form.user_type === "2" && this.selectedCustomer?.user_id) {
				return parseInt(this.selectedCustomer.user_id, 10);
			}
			return null;
		},
		paymentInUserId() {
			if (this.form.type !== "payment_in") return null;
			if (this.form.user_type === "1" && this.selectedDealer?.user_id) {
				return parseInt(this.selectedDealer.user_id, 10);
			}
			if (this.form.user_type === "2" && !this.isNewCustomer && this.selectedCustomer?.user_id) {
				return parseInt(this.selectedCustomer.user_id, 10);
			}
			return null;
		},
		userDueCreditDue() {
			const row = this.userDue?.[0] || this.userDue;
			return parseFloat(row?.credit_due_amount) || 0;
		},
		userDueCreditAvailable() {
			const row = this.userDue?.[0] || this.userDue;
			return parseFloat(row?.credit_available_amount) || 0;
		},
		paymentInDueBreakdown() {
			if (!this.form.apply_to_due) return null;
			const total = parseFloat(this.form.amount);
			const due = parseFloat(this.form.amount_for_due);
			if (isNaN(total) || isNaN(due)) return null;
			const deposit = Math.max(0, total - due);
			return {
				total: this.formatMoney(total),
				due: this.formatMoney(due),
				deposit: this.formatMoney(deposit),
			};
		},
		sumRenewalAllocated() {
			return this.sumMap(this.renewalAmounts);
		},
		renewalPaymentRows() {
			return this.flattenRenewalPaymentRows(this.renewalRows);
		},
		sumServiceAllocated() {
			return this.sumMap(this.serviceAmounts);
		},
		sumSalesAllocated() {
			return this.sumMap(this.salesAmounts);
		},
		showRepaymentAdvanceFromCredit() {
			return (
				this.form.type === "repayment" &&
				this.form.repayment_payment_type === "4" &&
				!this.hasPaymentFor("4")
			);
		},
		sumSelectedRepaymentAllocated() {
			let t = 0;
			if (this.hasPaymentFor("1")) t += this.sumRenewalAllocated;
			if (this.hasPaymentFor("2")) t += this.sumServiceAllocated;
			if (this.hasPaymentFor("4")) t += this.sumSalesAllocated;
			return Math.round(t * 100) / 100;
		},
		filteredDealers() {
			const q = String(this.dealerSearch || "").toLowerCase().trim();
			if (!q) return this.dealers.slice(0, 20);
			return this.dealers.filter((d) => String(d.first_name || "").toLowerCase().includes(q)).slice(0, 20);
		},
		filteredCustomers() {
			const q = String(this.customerSearch || "").toLowerCase().trim();
			if (!q) return this.customers.slice(0, 20);
			return this.customers
				.filter((c) => {
					const name = String(c.company_name || "").toLowerCase();
					const mobile = String(c.mobile || "").toLowerCase();
					return name.includes(q) || mobile.includes(q);
				})
				.slice(0, 20);
		},
	},
	watch: {
		showRepaymentAdvanceFromCredit: "onShowRepaymentAdvanceFromCreditChange",
	},
	created() {
		this.init();
	},
	beforeDestroy() {
		if (this.listKeywordDebounceTimer) {
			clearTimeout(this.listKeywordDebounceTimer);
			this.listKeywordDebounceTimer = null;
		}
		if (this.renewalSearchTimer) clearTimeout(this.renewalSearchTimer);
		if (this.serviceSearchTimer) clearTimeout(this.serviceSearchTimer);
		if (this.salesSearchTimer) clearTimeout(this.salesSearchTimer);
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([this.fetchBankAccounts(), this.fetchDealers(), this.fetchCustomers()]);
			this.fetchPaymentInList();
		},

		buildPaymentInQueryString() {
			const kw = String(this.paymentInList.keyword || "").trim();
			const date = String(this.paymentInList.date || "").trim();
			const bankId = String(this.paymentInList.bank_account_id || "").trim();
			const txnType =
				this.paymentInList.transaction_type !== "" && this.paymentInList.transaction_type != null
					? String(this.paymentInList.transaction_type)
					: "";
			// Backend expects these keys to exist (avoid PHP "Undefined array key").
			const parts = [
				`limit=${encodeURIComponent(this.paymentInList.listing_length)}`,
				`keyword=${encodeURIComponent(kw)}`,
				`date=${encodeURIComponent(date)}`,
				`bank_account_id=${encodeURIComponent(bankId)}`,
				`transaction_type=${encodeURIComponent(txnType)}`,
				"sort=created_at",
				`order=${encodeURIComponent(this.paymentInList.sort_date === "asc" ? "asc" : "desc")}`,
			];
			return parts.join("&");
		},

		paymentInListUrl() {
			return `/staffs/getPaymentIn?${this.buildPaymentInQueryString()}`;
		},

		onListKeywordInput() {
			if (this.listKeywordDebounceTimer) {
				clearTimeout(this.listKeywordDebounceTimer);
			}
			this.listKeywordDebounceTimer = setTimeout(() => {
				this.listKeywordDebounceTimer = null;
				this.fetchPaymentInList();
			}, 400);
		},

		fetchPaymentInList(pageUrl) {
			this.getToken();
			this.paymentInList.loading = true;
			const url = pageUrl || this.paymentInListUrl();
			this.paymentInList.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const payload = res.data?.data || {};
					const paginator = payload.paymentIn;
					if (!paginator) {
						this.paymentInList.rows = [];
						this.paymentInList.pagination = [];
						this.paymentInList.from = 0;
						this.paymentInList.to = 0;
						this.paymentInList.total = 0;
						return;
					}
					this.paymentInList.rows = paginator.data || [];
					this.paymentInList.pagination = paginator.links || [];
					this.paymentInList.from = paginator.from ?? 0;
					this.paymentInList.to = paginator.to ?? 0;
					this.paymentInList.total = paginator.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.paymentInList.rows = [];
					this.paymentInList.pagination = [];
					this.paymentInList.from = 0;
					this.paymentInList.to = 0;
					this.paymentInList.total = 0;
					this.$swal("Error", err.response?.data?.message || "Failed to load payment in list", "error");
				})
				.finally(() => {
					this.paymentInList.loading = false;
				});
		},

		onPaymentInPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchPaymentInList(`${base}${sep}${this.buildPaymentInQueryString()}`);
		},

		openPaymentInLogModal(row) {
			this.paymentInLogModal.row = row;
			this.paymentInLogModal.open = true;
			this.paymentInLogModal.logs = [];
			this.paymentInLogModal.error = "";
			this.fetchPaymentInDetail(row.id);
		},

		closePaymentInLogModal() {
			this.paymentInLogModal.open = false;
			this.paymentInLogModal.row = null;
			this.paymentInLogModal.logs = [];
			this.paymentInLogModal.loading = false;
			this.paymentInLogModal.error = "";
		},

		fetchPaymentInDetail(id) {
			if (id == null || id === "") {
				this.paymentInLogModal.error = "Invalid payment in id";
				return;
			}
			this.getToken();
			this.paymentInLogModal.loading = true;
			this.paymentInLogModal.error = "";
			this.$axios
				.get(`/staffs/getPaymentInDetail?id=${encodeURIComponent(id)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || res.data || {};
					const logs = data.paymentInTransaction;
					this.paymentInLogModal.logs = Array.isArray(logs) ? logs : [];
				})
				.catch((err) => {
					this.paymentInLogModal.logs = [];
					const msg = err?.response?.data?.message;
					this.paymentInLogModal.error =
						(typeof msg === "string" && msg) ||
						(msg && typeof msg === "object" ? Object.values(msg).flat().join(" ") : null) ||
						"Failed to load payment logs";
				})
				.finally(() => {
					this.paymentInLogModal.loading = false;
				});
		},

		openRevokePaymentModal(row) {
			if (!this.$canRevoke()) return;
			this.revokePaymentModal.row = row;
			this.revokePaymentModal.open = true;
			this.revokePaymentModal.submitting = false;
		},

		closeRevokePaymentModal() {
			this.revokePaymentModal.open = false;
			this.revokePaymentModal.row = null;
			this.revokePaymentModal.submitting = false;
		},

		isApiSuccess(data) {
			return data?.status === true || data?.status === "true" || data?.success === true || data?.success === "true";
		},

		async confirmRevokePaymentIn() {
			if (!this.revokePaymentModal.row?.id) return;
			this.getToken();
			this.revokePaymentModal.submitting = true;
			const formData = new FormData();
			formData.append("airo_payment_transaction_id", this.revokePaymentModal.row.id);
			try {
				const res = await this.$axios.post("/staffs/revokePaymentIn", formData, {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				});
				if (this.isApiSuccess(res.data)) {
					this.$swal("Success", res.data?.message || "Transaction revoked", "success");
					this.closeRevokePaymentModal();
					this.fetchPaymentInList(this.paymentInList.active_page_url);
					await this.refreshDealersAndCustomers();
				} else {
					this.$swal("Error", res.data?.message || "Failed to revoke transaction", "error");
				}
			} catch (err) {
				this.$swal("Error", err?.response?.data?.message || "Failed to revoke transaction", "error");
			} finally {
				this.revokePaymentModal.submitting = false;
			}
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		paymentTypeRecordLabel(v) {
			const map = { 1: "Payment In", 2: "Debit", 3: "Contra" };
			return map[Number(v)] || "—";
		},

		transactionTypeRecordLabel(v) {
			const map = { 1: "Contra", 2: "Deposit", 3: "Purchase", 4: "Sales", 13:"Renewal Repayment", 14:"Work Service Repayment", 15:"Other", 16:"Sales Repayment", 17:"Other Service Expense Sales" };
			return map[Number(v)] || "—";
		},

		userTypeRecordLabel(v) {
			const map = { 1: "Dealer", 2: "Customer", 3: "Vendor", 4: "Party", 5: "Staff" };
			return map[Number(v)] || "—";
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		fetchDealers() {
			return this.$axios
				.get("/staffs/dealers", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.dealers = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.dealers = [];
				});
		},

		fetchCustomers() {
			return this.$axios
				.get("/staffs/companies", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.customers = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.customers = [];
				});
		},

		refreshDealersAndCustomers() {
			return Promise.all([this.fetchDealers(), this.fetchCustomers()]);
		},

		hideDealerSuggestions() {
			setTimeout(() => {
				this.showDealerSuggestions = false;
			}, 200);
		},
		hideCustomerSuggestions() {
			setTimeout(() => {
				this.showCustomerSuggestions = false;
			}, 200);
		},

		selectDealer(dealer) {
			this.selectedDealer = dealer;
			this.dealerSearch = dealer?.first_name || "";
			this.showDealerSuggestions = false;
			if (this.form.type === "payment_in") {
				this.onPaymentInUserSelected();
			}
		},
		selectCustomer(c) {
			this.selectedCustomer = c;
			this.customerSearch = c?.company_name || "";
			this.showCustomerSuggestions = false;
			if (this.form.type === "payment_in") {
				this.onPaymentInUserSelected();
			}
		},

		enableNewCustomer() {
			this.isNewCustomer = true;
			this.selectedCustomer = null;
			this.customerSearch = "";
			this.showCustomerSuggestions = false;
			this.form.customer_name = "";
			this.form.customer_mobile = "";
			this.form.password = "123456";
			this.clearUserDueState();
			delete this.errors.customer_id;
		},

		selectExistingCustomer() {
			this.isNewCustomer = false;
			this.clearNewCustomerFields();
			this.clearUserDueState();
		},

		clearNewCustomerFields() {
			this.form.customer_name = "";
			this.form.customer_mobile = "";
			this.form.password = "123456";
			delete this.errors.customer_name;
			delete this.errors.customer_mobile;
			delete this.errors.password;
		},

		selectDealerForRepayment(dealer) {
			this.selectDealer(dealer);
			this.onRepaymentUserSelected();
		},
		selectCustomerForRepayment(c) {
			this.selectCustomer(c);
			this.onRepaymentUserSelected();
		},

		onTypeChange() {
			this.errors = {};
			if (this.form.type === "contra") {
				this.form.user_type = "";
				this.form.bank_account_id = "";
				this.clearRepaymentState();
				this.selectedDealer = null;
				this.selectedCustomer = null;
				this.dealerSearch = "";
				this.customerSearch = "";
				this.isNewCustomer = false;
				this.clearNewCustomerFields();
			} else if (this.form.type === "payment_in") {
				this.form.from_account_id = "";
				this.form.to_account_id = "";
				this.clearRepaymentState();
				this.clearUserDueState();
			} else if (this.form.type === "repayment") {
				this.form.from_account_id = "";
				this.form.to_account_id = "";
				if (!this.form.payment_date) {
					this.form.payment_date = this.todayIsoDate();
				}
				this.form.remarks = "";
				this.isNewCustomer = false;
				this.clearNewCustomerFields();
			} else {
				this.clearRepaymentState();
			}
		},

		todayIsoDate() {
			const d = new Date();
			const y = d.getFullYear();
			const m = String(d.getMonth() + 1).padStart(2, "0");
			const day = String(d.getDate()).padStart(2, "0");
			return `${y}-${m}-${day}`;
		},

		hasPaymentFor(code) {
			return Array.isArray(this.form.payment_fors) && this.form.payment_fors.map(String).includes(String(code));
		},

		clearRepaymentState() {
			this.form.payment_fors = [];
			this.form.repayment_payment_type = "";
			this.form.cash_online = "";
			this.form.cash_in_hand = "";
			this.form.amount_from_credit = "";
			this.renewalKeyword = "";
			this.serviceKeyword = "";
			this.salesKeyword = "";
			this.renewalRows = [];
			this.serviceRows = [];
			this.salesRows = [];
			this.renewalPagination = [];
			this.servicePagination = [];
			this.salesPagination = [];
			this.renewalAmounts = {};
			this.serviceAmounts = {};
			this.salesAmounts = {};
			this.totalRenewalPendingAmount = "0";
			this.totalServicePendingAmount = "0";
			this.totalSalesPendingAmount = "0";
			this.loadingRepaymentPending = false;
			this.refreshingRepaymentLists = false;
			this.clearRepaymentAdvanceUi();
		},

		clearUnselectedRepaymentAllocations() {
			if (!this.hasPaymentFor("1")) this.renewalAmounts = {};
			if (!this.hasPaymentFor("2")) this.serviceAmounts = {};
			if (!this.hasPaymentFor("4")) this.salesAmounts = {};
		},

		clearRepaymentAdvanceUi() {
			this.form.amount_from_credit = "";
			this.userCredit = null;
			this.userCreditError = "";
			this.userCreditLoading = false;
			this.prevShowRepaymentAdvance = false;
		},

		onRepaymentUserTypeChange() {
			this.onUserTypeChange();
			this.clearRepaymentPendingLists();
		},

		onRepaymentUserSelected() {
			if (!this.repaymentUserId) return;
			this.loadingRepaymentPending = true;
			this.clearRepaymentPendingLists();
			this.fetchPendingPayments();
		},

		clearRepaymentPendingLists() {
			this.renewalRows = [];
			this.serviceRows = [];
			this.salesRows = [];
			this.renewalPagination = [];
			this.servicePagination = [];
			this.salesPagination = [];
			this.renewalAmounts = {};
			this.serviceAmounts = {};
			this.salesAmounts = {};
			this.totalRenewalPendingAmount = "0";
			this.totalServicePendingAmount = "0";
			this.totalSalesPendingAmount = "0";
		},

		onRepaymentPaymentForChange() {
			this.errors = {};
			this.clearUnselectedRepaymentAllocations();
			if (this.hasPaymentFor("4") && this.form.repayment_payment_type === "4") {
				this.form.repayment_payment_type = "";
				this.clearRepaymentAdvanceUi();
			}
			this.syncRepaymentAmountFromAllocations();
			if (this.repaymentUserId && this.form.payment_fors.length) {
				this.fetchPendingPayments();
			}
		},

		onRepaymentPaymentTypeChange() {
			this.errors = {};
			const pt = this.form.repayment_payment_type;
			if (pt === "1") {
				this.form.cash_online = "";
			} else if (pt === "2") {
				this.form.cash_in_hand = "";
			} else if (pt === "4") {
				this.form.cash_online = "";
				this.form.cash_in_hand = "";
			} else {
				this.clearRepaymentAdvanceUi();
			}
		},

		async onShowRepaymentAdvanceFromCreditChange(visible) {
			if (!visible) {
				this.clearRepaymentAdvanceUi();
				return;
			}
			const firstOpen = !this.prevShowRepaymentAdvance;
			if (firstOpen && this.form.amount) {
				this.form.amount_from_credit = this.form.amount;
				this.errors.amount_from_credit = "";
			}
			await this.fetchUserCredit();
			this.prevShowRepaymentAdvance = true;
		},

		async fetchUserCredit() {
			if (!this.repaymentUserId) return;
			try {
				this.getToken();
				this.userCreditLoading = true;
				this.userCreditError = "";
				const response = await this.$axios.get("/staffs/getUserCredit", {
					params: { user_id: this.repaymentUserId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = response.data?.status === true || response.data?.status === "true";
				if (!ok) {
					this.userCredit = null;
					this.userCreditError = response.data?.message || "Failed to fetch credit balance";
					return;
				}
				this.userCredit = response.data?.data?.user_credit || null;
			} catch (e) {
				this.userCredit = null;
				this.userCreditError = e?.response?.data?.message || "Failed to fetch credit balance";
			} finally {
				this.userCreditLoading = false;
			}
		},

		normalizePendingPaginator(paginator) {
			if (!paginator || typeof paginator !== "object") {
				return { data: [], links: [] };
			}
			return {
				data: Array.isArray(paginator.data) ? paginator.data : [],
				links: Array.isArray(paginator.links) ? paginator.links : [],
			};
		},

		isPaidRenewalAiroPayment(payment) {
			const status = Number(payment.payment_status);
			const balance = parseFloat(payment.balance_amount);
			return status === 1 && !isNaN(balance) && balance === 0;
		},

		flattenRenewalPaymentRows(renewalRows) {
			const rows = [];
			const list = Array.isArray(renewalRows) ? renewalRows : [];
			for (const renewal of list) {
				if (!renewal || typeof renewal !== "object") continue;
				const payments = Array.isArray(renewal.airo_payments) ? renewal.airo_payments : [];
				for (const payment of payments) {
					if (!payment || typeof payment !== "object") continue;
					if (this.isPaidRenewalAiroPayment(payment)) continue;
					rows.push({
						...payment,
						renewal_id: renewal.id,
						imei: renewal.imei,
						vehicle_no: renewal.vehicle_no,
					});
				}
			}
			return rows;
		},

		pendingListUrl() {
			const p = new URLSearchParams();
			p.set("user_id", String(this.repaymentUserId));
			p.set("renewal_limit", String(this.renewalLimit));
			p.set("service_limit", String(this.serviceLimit));
			p.set("renewal_keyword", this.renewalKeyword || "");
			p.set("service_keyword", this.serviceKeyword || "");
			p.set("sales_limit", String(this.salesLimit));
			p.set("sales_keyword", this.salesKeyword || "");
			return `/staffs/userPendingPayments?${p.toString()}`;
		},

		fetchPendingPayments(renewalUrl, serviceUrl, salesUrl) {
			if (!this.repaymentUserId) return Promise.resolve();
			this.getToken();
			if (!renewalUrl && !serviceUrl && !salesUrl && !this.renewalRows.length && !this.serviceRows.length && !this.salesRows.length) {
				this.loadingRepaymentPending = true;
			} else {
				this.refreshingRepaymentLists = true;
			}
			let url = this.pendingListUrl();
			if (renewalUrl) url = renewalUrl;
			else if (serviceUrl) url = serviceUrl;
			else if (salesUrl) url = salesUrl;
			return this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					if (res.data.status !== true && res.data.status !== "true") {
						this.renewalRows = [];
						this.serviceRows = [];
						this.salesRows = [];
						return;
					}
					const d = res.data.data || {};
					this.totalRenewalPendingAmount = d.totalRenewalPendingAmount ?? "0";
					this.totalServicePendingAmount = d.totalServicePendingAmount ?? "0";
					this.totalSalesPendingAmount = d.totalSalesPendingAmount ?? "0";
					const rPag = this.normalizePendingPaginator(d.renewalPendingPayments);
					const sPag = this.normalizePendingPaginator(d.servicePendingPayments);
					const slPag = this.normalizePendingPaginator(d.salesPendingPayments);
					this.renewalRows = rPag.data;
					this.serviceRows = sPag.data;
					this.salesRows = slPag.data;
					this.renewalPagination = rPag.links;
					this.servicePagination = sPag.links;
					this.salesPagination = slPag.links;
					this.mergeRepaymentAmountMaps();
				})
				.catch((err) => {
					console.error(err);
					this.$swal("Error", err.response?.data?.message || "Failed to load pending payments", "error");
					this.renewalRows = [];
					this.serviceRows = [];
					this.salesRows = [];
				})
				.finally(() => {
					this.loadingRepaymentPending = false;
					this.refreshingRepaymentLists = false;
				});
		},

		onRenewalKeywordInput() {
			if (this.renewalSearchTimer) clearTimeout(this.renewalSearchTimer);
			this.renewalSearchTimer = setTimeout(() => {
				this.renewalSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		onServiceKeywordInput() {
			if (this.serviceSearchTimer) clearTimeout(this.serviceSearchTimer);
			this.serviceSearchTimer = setTimeout(() => {
				this.serviceSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		onSalesKeywordInput() {
			if (this.salesSearchTimer) clearTimeout(this.salesSearchTimer);
			this.salesSearchTimer = setTimeout(() => {
				this.salesSearchTimer = null;
				this.fetchPendingPayments();
			}, 400);
		},

		formatPaginationLabel(label) {
			if (label == null || label === "") return "";
			const el = document.createElement("textarea");
			el.innerHTML = String(label);
			return el.value;
		},

		onRenewalPage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(page.url);
		},

		onServicePage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(null, page.url);
		},

		onSalesPage(page) {
			if (!page.url) return;
			this.fetchPendingPayments(null, null, page.url);
		},

		mergeRepaymentAmountMaps() {
			for (const row of this.flattenRenewalPaymentRows(this.renewalRows)) {
				if (this.renewalAmounts[row.id] === undefined) this.renewalAmounts[row.id] = "";
			}
			for (const row of Array.isArray(this.serviceRows) ? this.serviceRows : []) {
				if (this.serviceAmounts[row.id] === undefined) this.serviceAmounts[row.id] = "";
			}
			for (const row of Array.isArray(this.salesRows) ? this.salesRows : []) {
				if (this.salesAmounts[row.id] === undefined) this.salesAmounts[row.id] = "";
			}
		},

		syncRepaymentAmountFromAllocations() {
			if (!this.form.payment_fors.length) return;
			const s = this.sumSelectedRepaymentAllocated;
			this.form.amount = s > 0 ? s.toFixed(2) : this.form.amount;
		},

		sumMap(obj) {
			let t = 0;
			if (!obj || typeof obj !== "object") return 0;
			for (const k of Object.keys(obj)) {
				const n = parseFloat(obj[k]);
				if (!isNaN(n) && n > 0) t += n;
			}
			return Math.round(t * 100) / 100;
		},

		renewalAiroPaymentForLabel(v) {
			const map = { 1: "Renewal", 8: "Extension" };
			return map[Number(v)] || (v != null && v !== "" ? `Type ${v}` : "—");
		},

		serviceTypeLabel(t) {
			const x = Number(t);
			return x === 1
				? "SIM Change"
				: x === 2
					? "Repair"
					: x === 3
						? "Replacement"
						: x === 4
							? "No Data"
							: x === 5
								? "Tracking Issue"
								: x === 6
									? "Hardware Issue"
									: x === 7
										? "Software Issue"
										: x === 8
											? "Alert Fail"
											: x === 9
												? "Other"
												: "N/A";
		},

		buildPairs(amountMap) {
			const pairs = [];
			for (const k of Object.keys(amountMap)) {
				const id = parseInt(k, 10);
				const v = parseFloat(amountMap[k]);
				if (!isNaN(id) && !isNaN(v) && v > 0) pairs.push([id, v]);
			}
			pairs.sort((a, b) => a[0] - b[0]);
			return {
				ids: pairs.map((p) => p[0]),
				amounts: pairs.map((p) => p[1]),
			};
		},

		onUserTypeChange() {
			this.errors = {};
			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.isNewCustomer = false;
			this.dealerSearch = "";
			this.customerSearch = "";
			this.clearUserDueState();
			if (this.form.user_type === "4") {
				this.form.customer_name = "";
				this.form.customer_mobile = "";
				this.form.password = "123456";
			} else {
				this.clearNewCustomerFields();
			}
		},

		clearUserDueState() {
			this.userDue = null;
			this.userDueLoading = false;
			this.userDueError = "";
			this.form.apply_to_due = false;
			this.form.amount_for_due = "";
			delete this.errors.amount_for_due;
		},

		onApplyToDueChange() {
			if (!this.form.apply_to_due) {
				this.form.amount_for_due = "";
				delete this.errors.amount_for_due;
				return;
			}
			const due = this.userDueCreditDue;
			const total = parseFloat(this.form.amount);
			if (!isNaN(total) && total > 0 && due > 0) {
				this.form.amount_for_due = String(Math.min(total, due));
			}
		},

		onPaymentInUserSelected() {
			this.clearUserDueState();
			if (this.paymentInUserId) {
				this.fetchUserDue(this.paymentInUserId);
			}
		},

		async fetchUserDue(userId) {
			this.getToken();
			this.userDueLoading = true;
			this.userDueError = "";
			this.userDue = null;
			try {
				const res = await this.$axios.get("/staffs/getUserDue", {
					params: { user_id: userId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (!ok) {
					this.userDueError = this.$getApiErrorMessage(res.data, "Failed to load due balance");
					return;
				}
				const rows = res.data?.data?.userDue;
				this.userDue = Array.isArray(rows) ? rows : rows ? [rows] : [];
			} catch (e) {
				this.userDueError = this.$getApiErrorMessage(e, "Failed to load due balance");
			} finally {
				this.userDueLoading = false;
			}
		},

		validateRepayment() {
			this.errors = {};
			const eps = 0.02;
			const amount = parseFloat(this.form.amount);
			if (isNaN(amount) || amount < 0) {
				this.errors.amount = "Enter a valid amount";
				return false;
			}
			if (!this.form.payment_fors.length) {
				this.errors.payment_for = "Select at least one Payment for";
				return false;
			}
			if (!this.form.payment_date) {
				this.errors.payment_date = "Select payment date";
				return false;
			}
			if (!this.form.user_type || (this.form.user_type !== "1" && this.form.user_type !== "2")) {
				this.errors.user_type = "Select user type";
				return false;
			}
			if (!this.form.bank_account_id) {
				this.errors.bank_account_id = "Select bank account";
				return false;
			}
			if (this.form.user_type === "1" && !this.selectedDealer?.user_id) {
				this.errors.dealer_id = "Select dealer";
				return false;
			}
			if (this.form.user_type === "2" && !this.selectedCustomer?.user_id) {
				this.errors.customer_id = "Select customer";
				return false;
			}
			if (!this.form.repayment_payment_type) {
				this.errors.repayment_payment_type = "Select payment type";
				return false;
			}
			const pt = this.form.repayment_payment_type;
			if (pt === "4" && this.hasPaymentFor("4")) {
				this.errors.repayment_payment_type = "Advance payment is not allowed when sales repayment is selected";
				return false;
			}
			if (pt === "4") {
				const creditAmt = parseFloat(this.form.amount_from_credit);
				if (!this.form.amount_from_credit || isNaN(creditAmt) || creditAmt <= 0) {
					this.errors.amount_from_credit = "Enter amount from advance";
					return false;
				}
				if (this.userCredit && this.userCredit.credit_available_amount != null) {
					const available = parseFloat(this.userCredit.credit_available_amount);
					if (!isNaN(available) && creditAmt > available) {
						this.errors.amount_from_credit = "Amount from advance cannot exceed available advance";
						return false;
					}
				}
				if (Math.abs(creditAmt - amount) > eps) {
					this.$swal(
						"Validation",
						`Amount from advance (₹${creditAmt.toFixed(2)}) must match total amount (₹${amount.toFixed(2)}).`,
						"warning"
					);
					return false;
				}
			}
			if (pt === "1" || pt === "3") {
				const c = parseFloat(this.form.cash_in_hand);
				if (isNaN(c) || c < 0) {
					this.errors.cash_in_hand = "Enter cash amount";
					return false;
				}
			}
			if (pt === "2" || pt === "3") {
				const o = parseFloat(this.form.cash_online);
				if (isNaN(o) || o < 0) {
					this.errors.cash_online = "Enter online amount";
					return false;
				}
			}

			let allocatedSum = 0;
			let hasAnyAllocation = false;
			if (this.hasPaymentFor("1")) {
				const { ids, amounts } = this.buildPairs(this.renewalAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one renewal payment", "warning");
					return false;
				}
				allocatedSum += amounts.reduce((a, b) => a + b, 0);
				hasAnyAllocation = true;
			}
			if (this.hasPaymentFor("2")) {
				const { ids, amounts } = this.buildPairs(this.serviceAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one work service", "warning");
					return false;
				}
				allocatedSum += amounts.reduce((a, b) => a + b, 0);
				hasAnyAllocation = true;
			}
			if (this.hasPaymentFor("4")) {
				const { ids, amounts } = this.buildPairs(this.salesAmounts);
				if (!ids.length) {
					this.$swal("Validation", "Enter amount for at least one sale", "warning");
					return false;
				}
				allocatedSum += amounts.reduce((a, b) => a + b, 0);
				hasAnyAllocation = true;
			}
			if (!hasAnyAllocation) {
				this.$swal("Validation", "Allocate amounts for the selected repayment types", "warning");
				return false;
			}
			allocatedSum = Math.round(allocatedSum * 100) / 100;
			if (Math.abs(allocatedSum - amount) > eps) {
				this.$swal(
					"Validation",
					`Total amount must match sum of all selected allocations (₹${allocatedSum.toFixed(2)}).`,
					"warning"
				);
				return false;
			}
			if (pt === "1" || pt === "3") {
				const cash = parseFloat(this.form.cash_in_hand) || 0;
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(cash + online - amount) > eps) {
					this.$swal("Validation", `Cash + online must equal total amount (₹${amount.toFixed(2)}).`, "warning");
					return false;
				}
			}
			if (pt === "2") {
				const online = parseFloat(this.form.cash_online) || 0;
				if (Math.abs(online - amount) > eps) {
					this.$swal("Validation", "Online amount must equal total amount for online-only payment.", "warning");
					return false;
				}
			}
			return true;
		},

		validate() {
			this.errors = {};
			if (!this.form.type) {
				this.errors.type = "Select payment type";
				return false;
			}
			if (this.form.type === "repayment") {
				return this.validateRepayment();
			}
			const amt = parseFloat(this.form.amount);
			if (isNaN(amt) || amt < 0) {
				this.errors.amount = "Enter a valid amount";
				return false;
			}
			if (!this.form.payment_date) {
				this.errors.payment_date = "Select payment date";
				return false;
			}

			if (this.form.type === "contra") {
				if (!this.form.from_account_id) {
					this.errors.from_account_id = "Select from account";
					return false;
				}
				if (!this.form.to_account_id) {
					this.errors.to_account_id = "Select to account";
					return false;
				}
				if (String(this.form.from_account_id) === String(this.form.to_account_id)) {
					this.errors.same_account = "From and To bank accounts cannot be same";
					return false;
				}
			}

			if (this.form.type === "payment_in") {
				if (!this.form.user_type) {
					this.errors.user_type = "Select user type";
					return false;
				}
				if (!this.form.bank_account_id) {
					this.errors.bank_account_id = "Select bank account";
					return false;
				}
				if (this.form.user_type === "1") {
					if (!this.selectedDealer?.user_id) {
						this.errors.dealer_id = "Select dealer";
						return false;
					}
				}
				if (this.form.user_type === "2") {
					if (this.isNewCustomer) {
						if (!String(this.form.customer_name || "").trim()) {
							this.errors.customer_name = "Enter customer name";
							return false;
						}
						if (!String(this.form.customer_mobile || "").trim()) {
							this.errors.customer_mobile = "Enter customer mobile";
							return false;
						}
						if (!String(this.form.password || "").trim()) {
							this.errors.password = "Password is required";
							return false;
						}
					} else if (!this.selectedCustomer?.user_id) {
						this.errors.customer_id = "Select customer";
						return false;
					}
				}
				if (this.form.apply_to_due) {
					const dueAmt = parseFloat(this.form.amount_for_due);
					if (isNaN(dueAmt) || dueAmt <= 0) {
						this.errors.amount_for_due = "Enter amount for due";
						return false;
					}
					if (dueAmt > amt + 0.01) {
						this.errors.amount_for_due = "Amount for due cannot exceed payment amount";
						return false;
					}
					if (dueAmt > this.userDueCreditDue + 0.01) {
						this.errors.amount_for_due = "Amount for due cannot exceed outstanding due";
						return false;
					}
				}
			}

			return true;
		},

		resetAll() {
			this.errors = {};
			this.submitting = false;
			this.dealerSearch = "";
			this.customerSearch = "";
			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.isNewCustomer = false;
			this.showDealerSuggestions = false;
			this.showCustomerSuggestions = false;
			this.clearRepaymentState();
			this.clearUserDueState();
			this.form = {
				type: "",
				user_type: "",
				amount: "",
				payment_id: "",
				payment_date: "",
				remarks: "",
				from_account_id: "",
				to_account_id: "",
				bank_account_id: "",
				customer_name: "",
				customer_mobile: "",
				password: "123456",
				apply_to_due: false,
				amount_for_due: "",
				payment_fors: [],
				repayment_payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
			};
		},

		buildUserPaymentPayload() {
			const paymentFors = (this.form.payment_fors || []).map((v) => parseInt(v, 10)).filter((n) => [1, 2, 4].includes(n));
			const payload = {
				user_id: this.repaymentUserId,
				amount: parseFloat(this.form.amount),
				payment_type: parseInt(this.form.repayment_payment_type, 10),
				// Array when multi-select; single int when only one (legacy-compatible)
				payment_for: paymentFors.length === 1 ? paymentFors[0] : paymentFors,
				payment_user_type: parseInt(this.form.user_type, 10),
				payment_date: this.form.payment_date,
			};
			const pt = this.form.repayment_payment_type;
			if (pt === "4") {
				payload.amount_from_credit = parseFloat(this.form.amount_from_credit) || 0;
				payload.cash_online = null;
				payload.payment_id = null;
				payload.bank_account_id = null;
				payload.cash_in_hand = null;
			} else {
				payload.amount_from_credit = null;
				if (pt === "2" || pt === "3") {
					payload.cash_online = parseFloat(this.form.cash_online) || 0;
					payload.payment_id = String(this.form.payment_id || "").trim();
					payload.bank_account_id = this.form.bank_account_id ? parseInt(this.form.bank_account_id, 10) : null;
				} else {
					payload.cash_online = null;
					payload.payment_id = null;
					payload.bank_account_id = null;
				}
				if (pt === "1" || pt === "3") {
					payload.cash_in_hand = parseFloat(this.form.cash_in_hand) || 0;
				} else {
					payload.cash_in_hand = null;
				}
			}
			if (this.hasPaymentFor("1")) {
				const { ids, amounts } = this.buildPairs(this.renewalAmounts);
				payload.airo_payment_ids = ids;
				payload.airo_payment_amounts = amounts;
			}
			if (this.hasPaymentFor("2")) {
				const { ids, amounts } = this.buildPairs(this.serviceAmounts);
				payload.service_ids = ids;
				payload.service_amounts = amounts;
			}
			if (this.hasPaymentFor("4")) {
				const { ids, amounts } = this.buildPairs(this.salesAmounts);
				payload.sales_ids = ids;
				payload.sales_amounts = amounts;
			}
			return payload;
		},

		async submitRepayment() {
			const payload = this.buildUserPaymentPayload();
			const res = await this.$axios.post("/staffs/userPayment", payload, {
				headers: { Authorization: "Bearer " + this.token },
			});
			const ok = res.data?.status === true || res.data?.status === "true";
			if (ok) {
				this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Repayment recorded", "success");
				this.resetAll();
				this.fetchPaymentInList();
				await this.refreshDealersAndCustomers();
			} else {
				this.$applyApiFieldErrors(this.errors, res.data);
				this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to record repayment"), "error");
			}
		},

		async submit() {
			if (this.submitting) return;
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();
			try {
				if (this.form.type === "repayment") {
					await this.submitRepayment();
					return;
				}
				if (this.form.type === "contra") {
					const payload = {
						amount: parseFloat(this.form.amount),
						from_account_id: parseInt(this.form.from_account_id, 10),
						to_account_id: parseInt(this.form.to_account_id, 10),
						remarks: String(this.form.remarks || "").trim() || null,
						payment_date: this.form.payment_date,
						payment_id: String(this.form.payment_id || "").trim() || null,
					};
					const res = await this.$axios.post("/staffs/contraPayment", payload, {
						headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
					});
					const ok = res.data?.status === true || res.data?.status === "true";
					if (ok) {
						this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Contra payment created", "success");
						this.resetAll();
						this.fetchPaymentInList();
						await this.refreshDealersAndCustomers();
					} else {
						this.$applyApiFieldErrors(this.errors, res.data);
						this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to create contra payment"), "error");
					}
					return;
				}

				// payment in
				const payload = {
					user_type: parseInt(this.form.user_type, 10),
					amount: parseFloat(this.form.amount),
					payment_id: String(this.form.payment_id || "").trim() || null,
					bank_account_id: parseInt(this.form.bank_account_id, 10),
					payment_date: this.form.payment_date,
					remarks: String(this.form.remarks || "").trim() || null,
					customer_name: null,
					customer_mobile: null,
					password: null,
					dealer_id: null,
					customer_id: null,
				};

				if (this.form.user_type === "1") {
					payload.dealer_id = parseInt(this.selectedDealer.user_id, 10);
				} else if (this.form.user_type === "2") {
					if (this.isNewCustomer) {
						payload.customer_name = String(this.form.customer_name || "").trim();
						payload.customer_mobile = String(this.form.customer_mobile || "").trim();
						payload.password = String(this.form.password || "").trim();
					} else {
						payload.customer_id = parseInt(this.selectedCustomer.user_id, 10);
					}
				} else if (this.form.user_type === "4") {
					payload.customer_name = String(this.form.customer_name || "").trim() || null;
					payload.customer_mobile = String(this.form.customer_mobile || "").trim() || null;
				}

				if (this.form.apply_to_due) {
					payload.apply_to_due = 1;
					payload.amount_for_due = parseFloat(this.form.amount_for_due);
				} else {
					payload.apply_to_due = null;
					payload.amount_for_due = null;
				}

				const res = await this.$axios.post("/staffs/paymentIn", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Payment created", "success");
					this.resetAll();
					this.fetchPaymentInList();
					await this.refreshDealersAndCustomers();
				} else {
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to create payment"), "error");
				}
			} catch (err) {
				this.$applyApiFieldErrors(this.errors, err);
				this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>

